import { Code } from "@phosphor-icons/react"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { BASE_PATH } from "@/constants/app.mjs"
import { productPageLogos } from "@/constants/productPageLogos"
import NicerBlockquote from "../../common/NicerBlockquote"
import ProductPageHeader from "../ProductPageHeader"
import ProductPageTemplate from "../ProductPageTemplate"
import {
  CustomerLogoStrip,
  FeatureCard,
  ProductCTASection,
  StatsGrid,
  ThreeFeaturesSection,
} from "../shared"
import { APIResponseViewer } from "./APIResponseViewer"
import { JupyterNotebookViewer } from "./JupyterNotebookViewer"
import { PythonCodeViewer } from "./PythonCodeViewer"

const APIPage = () => {
  return (
    <ProductPageTemplate
      header={
        <ProductPageHeader
          icon={Code}
          title="API"
          subtitle="Access Token Terminal's metrics, projects, and financial data through 25+ endpoints."
          ctaButtons={
            <>
              <LinkButton
                key="subscribe-to-pro"
                size="large"
                variant="solid"
                href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
                style={{ zIndex: 4 }}
              >
                Book a demo
              </LinkButton>
              <LinkButton
                key="read-the-docs"
                size="large"
                variant="ghost"
                href="https://tokenterminal.com/docs/api-reference/introduction"
                style={{ zIndex: 4 }}
              >
                Read the docs
              </LinkButton>
            </>
          }
          backgroundImageUrl={`/${BASE_PATH}static/images/marketing/home/hero-illustration-construction.webp`}
          whoWeServeText="Developers and data engineers building applications, dashboards, or automated pipelines."
          simpleWhoWeServeText="developers and data engineers"
        />
      }
      productId="api"
    >
      {/* Customer Logo Strip */}
      <CustomerLogoStrip logos={productPageLogos.api} />

      {/* Three Features Section */}
      <ThreeFeaturesSection
        features={[
          {
            component: (
              <FeatureCard>
                <APIResponseViewer />
              </FeatureCard>
            ),
            title: (
              <>
                25+ endpoints,
                <br />
                one integration
              </>
            ),
            description:
              "Access metrics, projects, financial statements, and market sectors through a clean REST API.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <PythonCodeViewer />
              </FeatureCard>
            ),
            title: "Build custom metric combinations",
            description:
              "Combine metrics programmatically to create custom ratios and indicators for your models.",
          },
          {
            component: (
              <FeatureCard className="h-[300px]">
                <JupyterNotebookViewer />
              </FeatureCard>
            ),
            title: "Integrate into notebooks and pipelines",
            description:
              "Works with Python, JavaScript, or any language. Built for Jupyter notebooks and production systems.",
          },
        ]}
      />

      {/* Stats Grid */}
      <div className="mt-32">
        <StatsGrid
          stats={[
            {
              value: "20+",
              label: "API Endpoints",
            },
            {
              value: "250k",
              label: "Daily Requests",
            },
            {
              value: (
                <>
                  2+ <span className="text-fg-secondary">PB</span>
                </>
              ),
              label: "Data Warehouse",
            },
            {
              value: "99.9%",
              label: "Uptime",
            },
          ]}
        />
      </div>

      {/* Customer Quote */}
      <div className="max-w-[900px] mx-auto mt-32">
        <NicerBlockquote
          content="Token Terminal is the essential input to our real-time investing model. It's like building PE-grade models with streaming POS data."
          author="John McNiff"
          authorTitle="Co-founder at Theia"
          authorImage={`/${BASE_PATH}static/images/marketing/home/testimonials/avatar-john-theia.jpg`}
        />
      </div>

      {/* CTA Section */}
      <ProductCTASection
        tiers={[
          {
            id: "docs",
            title: "Explore the documentation",
            features: [
              "20+ REST API endpoints",
              "Comprehensive parameter documentation",
              "Code examples in multiple languages",
              "Real-time data feeds",
            ],
            cta: (
              <LinkButton
                className="w-full mt-4"
                size="large"
                href="https://tokenterminal.com/docs/api-reference/introduction"
                target="_blank"
              >
                View documentation
              </LinkButton>
            ),
          },
          {
            id: "api",
            title: "Get API access",
            badge: { label: "API", color: "green" },
            features: [
              "Includes Pro Access to Explorer",
              "REST APIs for metrics, projects, and more",
              "250,000 requests/day",
              "Discounts for startups",
            ],
            cta: (
              <LinkButton
                className="w-full mt-4"
                size="large"
                href="https://calendly.com/d/cv8h-mgf-n3g/token-terminal-api-demo"
                color="success"
              >
                Book a demo
              </LinkButton>
            ),
          },
        ]}
      />
    </ProductPageTemplate>
  )
}

export default APIPage
